import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    InputSignal,
    OnDestroy,
    Signal,
    WritableSignal,
    effect,
    inject,
    input,
    untracked,
    computed,
    signal,
} from '@angular/core';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import {
    McMciDataArraySchema,
    IDatiqMapGeoJsonFeature,
    IIqMapGaugeLegend,
} from '../models';
import { MapService } from '../state';
import { get } from 'lodash';
import { z } from 'zod';
import { IqMcEventBusStateAreaService, IqMcEventBusStateMciService, TimeframeRouteMap } from "@dat/iq-mc-event-bus";

const MciDataPayloadSchema = z.object({
    id: z.string(),
    loaded: z.boolean(),
    equipmentType: z.string(),
    timeframe: z.string(),
    direction: z.string(),
    zoom: z.number().optional().default(4),
    capacityMap: z
        .record(
            z.string(),
            z.object({
                high: z.number(),
                low: z.number(),
                color: z.string().nullable(),
                index: z.number(),
            }),
        )
        .default({}),
    mciData: McMciDataArraySchema,
});

type MciDataPayload = z.infer<typeof MciDataPayloadSchema>;

/**
 * The reason this doesn't have a schema is because these are
 * usually huge payloads and validating them will just be hard to
 * debug and set up.
 */
type LocationPayload = {
    id: string;
    loaded: boolean;
    featureCollection: IDatiqMapGeoJsonFeature;
    latLong: { lat: number; lng: number };
};

@Component({
    selector: 'iq-mc-map-mci-dashboard',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './iq-mc-map-mci-dashboard.component.html',
    styleUrl: './iq-mc-map-mci-dashboard.component.scss',
})
export class IqMcMapMciDashboardComponent implements OnDestroy {
    id: InputSignal<string> = input('');
    mapLoaded: WritableSignal<boolean> = signal<boolean>(false);

    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );
    private _map: MapService = inject(MapService);
    private _ref: ElementRef = inject(ElementRef);
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _deregister: Record<string, IqEventBusBrokerHandler> = {};

    private _state = inject(IqMcEventBusStateAreaService);
    private _mciState = inject(IqMcEventBusStateMciService);

    //# region Signals
    dataPayload: WritableSignal<MciDataPayload> = signal(null);
    featurePayload: Signal<any> = this._state.getArea('STATE');

    featureCollection = computed(() => this.featurePayload().featureCollection);

    //# endregion

    isMapLoaded: Signal<boolean> = computed(() => {
        const dataPayload = this.dataPayload();
        const featurePayload = this.featurePayload();

        return dataPayload?.loaded && featurePayload?.loaded;
    });

    timeFrame: Signal<string> = computed(() => {
        if (!this.dataPayload()?.loaded) return '--';

        return (
            {
                ['THIRTY_DAYS_BACK']: 'Prior 30 Days',
            }[this.dataPayload().timeframe] ?? '--'
        );
    });

    mapGaugeLegend: Signal<IIqMapGaugeLegend> = computed(() => {
        if (!this.dataPayload()?.capacityMap) return null;
        const capacityMap = this.dataPayload().capacityMap;
        const capacities = Object.values(capacityMap);
        const rangeLabelPaths: string[] = [
            '0.low',
            '0.high',
            '1.high',
            '2.high',
            '3.high',
            '4.high',
            '5.high',
            '6.high',
        ];
        const range: string[] = rangeLabelPaths.map((path) => {
            return this._setRangeLabel(get(capacities, path));
        });
        const colors: string[] = capacities.map((capacity) => capacity.color);

        return {
            range,
            colors,
        };
    });

    private _setRangeLabel(range: number) {
        if (isNaN(range)) return '';
        return `${range < 0 ? range : '+' + range}`;
    }

    constructor() {
        this._deregister = this._bus.registerMap({
            setMapDashboardMciData: {
                callback: (payload: MciDataPayload) => {
                    if (payload.id !== this.id()) return;

                    const test: any = MciDataPayloadSchema.safeParse(payload);

                    if (!test.success) {
                        console.warn(
                            'IqMcMapMciDashboardComponent Invalid Payload',
                        );
                        console.log(test?.error?.issues);
                        return;
                    }

                    this.dataPayload.set(payload);
                },
            },
        });

        effect(() => {
            /**
             * Checks if the lane is loaded
             *
             * A lane is loaded once the features and coordinates
             * are set up.
             */
            const dataPayload = this.dataPayload();
            const featurePayload = this.featurePayload();

            if (!dataPayload?.loaded || !featurePayload?.loaded) return;

            untracked(async () => {
                /**
                 * Loads a clears features
                 */
                const map: google.maps.Map = await this._map.load(
                    this._ref,
                    dataPayload.zoom,
                );

                /**
                 * Sets the features with their data
                 */
                const areas = await this._map.loadMciFeatures(
                    featurePayload?.featureCollection,
                    dataPayload.mciData,
                );

                areas.addListener(
                    'click',
                    (event: google.maps.Data.MouseEvent) => {
                        const feature = event.feature;
                        const { equipmentType, timeframe, direction } = dataPayload;

                        const payload = {
                            id: this.id(),
                            componentName: 'IqMcMapMciDashboardComponent',
                            code: feature.getProperty('code'),
                            mci: feature.getProperty('mci'),
                            capacity: feature.getProperty('capacity'),
                            equipmentType: equipmentType,
                            timeframe: timeframe,
                            direction: direction,
                            url: `/market-conditions/conditions/${equipmentType}~STATE~${TimeframeRouteMap[timeframe]}~${direction}~${feature.getProperty('code')}~~0`,
                        };


                        /**
                         * This dispatches an event when the component clicks on a search
                         *
                         *
                         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
                         *
                         * ```json
                         * {
                         *  "onClick": [
                         *    {
                         *      "name": "EVENT_NAME",
                         *      "payload": {},
                         *      "sourceEventRule": {
                         *          "payload": [
                         *            {
                         *                "path": "id",
                         *                "operator": "eq",
                         *                "value": "COMPONENT_ID"
                         *            },
                         *            {
                         *                "path": "componentName",
                         *                "operator": "eq",
                         *                "value": "IqMcMapMciDashboardComponent"
                         *            },
                         *          ]
                         *      },
                         *      "rules": []
                         *    },
                         *   ],
                         * }
                         * ```
                         */
                        const dispatch = () =>
                            this._bus.dispatch({
                                name: 'navigateToUrl',
                                payload,
                            });

                        dispatch();
                    },
                );

                /**
                 * Once all features and directions are loaded, set the map to loaded
                 */
                this.mapLoaded.set(true);
            });
        });
    }

    ngOnDestroy() {
        this._eventBus.unregisterMap(this._deregister);
    }
}
